import type { ProgramProgressCell } from '../types/domain';
import type { ProgramContentWorksheetOwnerRef } from '../utils/programContentWorksheetOwner';
import { formatLearningTextForDisplay, parseMemoFieldStorageDeep } from './programCellDisplayText';
import { resolveWorksheetRefIdFromLearningText } from './programContentWorksheetOwner';
import type { WorksheetRefLinkState } from './programWorksheetLinkState';

export type ProgramWorksheetListItem = {
  annualCellKey: string;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  templateLabel: string;
  timetableText: string;
  unitText: string;
  learningText: string;
  sortKey: string;
  worksheetOwner: ProgramContentWorksheetOwnerRef | null;
  isDraftOnly: boolean;
  hasWorksheetRef?: boolean;
  hasLocalDraft?: boolean;
  hasDbDocument?: boolean;
  /** localStorage 초안 조회 키(동기화 미러 시 대표 annualCellKey) */
  draftStorageRowKey?: string;
  /** 동기화 미러 행 — 목록에서 비활성 */
  isSyncMirror?: boolean;
  linkState?: WorksheetRefLinkState | null;
  linkStateLabel?: string;
  pageCount?: number;
  documentBytes?: number;
};

export function formatWorksheetDocumentBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10_240 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function sortWorksheetListItems(items: ProgramWorksheetListItem[]): ProgramWorksheetListItem[] {
  return [...items].sort((a, b) => a.sortKey.localeCompare(b.sortKey, 'en'));
}

export function worksheetListItemOwnerKey(item: ProgramWorksheetListItem): string | null {
  if (!item.worksheetOwner?.id?.trim()) return null;
  return `worksheet_ref:${item.worksheetOwner.id.trim()}`;
}

export type WorksheetListOrphanLookup = {
  cellById?: Map<string, ProgramProgressCell>;
  periodLabelForSlot?: (slotIndex: number) => string;
  weekdayLabelForKey?: (weekdayKey: string) => string;
  shortDateLabelForIso?: (iso: string) => string;
};

export function buildWorksheetListItemFromAnnualRow(params: {
  annualCellKey: string;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  templateLabel: string;
  timetableText: string;
  unitText: string;
  /** learning_text 원본(JSON·ref 포함) — 연결 상태 판별용 */
  learningTextRaw: string;
  sortKey: string;
  isDraftOnly?: boolean;
  hasLocalDraft?: boolean;
  hasDbDocument?: boolean;
  draftStorageRowKey?: string;
  isSyncMirror?: boolean;
}): ProgramWorksheetListItem {
  const worksheetRefId = resolveWorksheetRefIdFromLearningText(params.learningTextRaw);
  const draftStorageRowKey = (params.draftStorageRowKey ?? params.annualCellKey).trim();
  return {
    annualCellKey: params.annualCellKey,
    dateLabel: params.dateLabel,
    weekdayLabel: params.weekdayLabel,
    periodLabel: params.periodLabel,
    templateLabel: params.templateLabel,
    timetableText: params.timetableText,
    unitText: params.unitText,
    learningText: learningTextSnippetForWorksheetList(params.learningTextRaw),
    sortKey: params.sortKey,
    worksheetOwner: worksheetRefId ? { kind: 'worksheet_ref', id: worksheetRefId } : null,
    isDraftOnly: !!params.isDraftOnly,
    hasWorksheetRef: !!worksheetRefId,
    hasLocalDraft: !!params.hasLocalDraft,
    hasDbDocument: !!params.hasDbDocument,
    draftStorageRowKey,
    isSyncMirror: !!params.isSyncMirror,
  };
}

export function learningTextSnippetForWorksheetList(raw: string): string {
  return formatLearningTextForDisplay(parseMemoFieldStorageDeep(raw ?? '').text).trim();
}
